<script lang="ts">
  import {Tippy} from '@components';
  import type {Item} from '@/types/metadata';
  import {createEventDispatcher} from 'svelte';
  import {Toggle, Range, UploadImage, File} from './components';
  import {preview} from '../../store';

  import Dropdown from './Dropdown.svelte';
  const dispatch = createEventDispatcher();
  type FIELD_TYPE = 'text' | 'file' | 'color' | 'image' | 'range' | 'toggle' | 'dropdown' | 'textarea';

  export let value: any;
  export let label: string = '';
  export let error: string = '';
  export let loading: boolean = false;
  export let items: Array<Item> = [];
  export let isFont: boolean = false;
  export let isMusic: boolean = false;
  export let isClearable: boolean = false;
  export let assets: Array<any> = [];
  export let placeholder: string = '';
  export let required: boolean = false;
  export let hasMarginBottom: boolean = false;
  export let stackedLabel: boolean = true;
  export let type: FIELD_TYPE = 'text';
  export let dynamic: boolean = false;
  export let key: string | null = null;
  export let divId: string = '';
  export let minValue = 0;
  export let maxValue = 100;
  export let step = 1;

  const imageChanged = (e: CustomEvent) => {
    dispatch('image-changed', {
      file: e.detail.file
    });
  };

  const onChangeFn = () => {
    dispatch('change', {
      value
    });
  };

  // [IMPORTANT: DO NOT DELETE / REMOVE] This is an input buffer, the reason we need to do this is because rapid updates of the dom will cause CPUs to be overloaded. We do this for values that change rapidly. i.e. audio volume and/or color input because the change is very rapid that the cpu can not keep up.
  let inputTimeout: NodeJS.Timeout | null = null;

  const debounce = (e: any) => {
    if (inputTimeout) {
      window.clearTimeout(inputTimeout);
    }

    // Important: do not delete/remove
    inputTimeout = setTimeout(() => {
      if (!key) value = e.target.value;
      const extraInformation = $preview.extraInformation;
      if (extraInformation) {
        const information = extraInformation.find(item => item.key == key);
        if (information) {
          information.value = e.target.value;
          preview.set({extraInformation});
        }
      }
    }, 400); // Changing the timeout value will make it more responsive, however it will cause higher loads on CPUs. 200 is baseline.
  };

  const deriveValue = (input: any) => {
    if (isFont) {
      return {...input};
    } else {
      return input.value;
    }
  };

  const deriveLabel = (input: any) => {
    if (isFont) {
      return input.label;
    } else {
      if (!input) return placeholder;
      if (isMusic && assets) {
        const asset = assets.find(item => item.file.publicUrl == input);
        if (asset) return asset.name;
        else {
          try {
            return items.find(item => item.value == input)?.label;
          } catch {
            return input;
          }
        }
      }
      return input;
    }
  };
</script>

<div class="flex w-full flex-col gap-4">
  <div class="field-group flex items-center" class:mb-12={hasMarginBottom} class:field-group-stacked={stackedLabel}>
    <!-- LABEL -->
    {#if label}
      <div class="w-full" class:mb-2={stackedLabel}>
        <div class="text-sm text-gray-500">
          {label}
          {#if required}
            <span class="ml-1 text-red-500">*</span>
          {/if}
          <!-- Aside -->
          {#if divId}
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <!-- <div
              class="dot"
              id={divId}
              on:click={() => dispatch('toggle-aside', { divId: divId })}
            /> -->
            <div>
              <i class="fa-solid fa-circle-info" style="color: #D4D4D8;"></i>
            </div>
          {/if}
        </div>
      </div>
    {/if}
    <!-- INPUT -->
    <div class="relative flex-grow" class:w-full={stackedLabel}>
      {#if type === 'text'}
        <input
          type="text"
          {value}
          on:input={debounce}
          {required}
          placeholder="Add Text Here"
          class="w-full rounded-md border border-gray-100 px-4 py-2 text-sm focus-visible:outline-gray-300"
        />
      {:else if type === 'image'}
        <UploadImage on:image-changed={imageChanged} bind:imgSrc={value} bind:loading />
      {:else if type === 'color'}
        <input {value} on:input={debounce} type="color" class="aspect-square h-10 w-10 cursor-pointer rounded border" />
      {:else if type === 'toggle'}
        <div class="pr-1">
          <Toggle bind:active={value} />
        </div>
      {:else if type === 'dropdown'}
        <!-- <Tippy bind:value {placeholder} {isFont} {items} /> -->
        <Dropdown {items} {placeholder} {isFont} bind:value {deriveValue} {deriveLabel} {isClearable} />
      {:else if type === 'range'}
        <Range bind:value on:change={onChangeFn} bind:step bind:minValue bind:maxValue />
      {:else if type === 'file'}
        <File bind:value bind:error />
      {/if}
    </div>
  </div>
  <!-- INPUT ERROR -->
  {#if error}
    <div class="mt-2 px-2 text-sm text-red-500">{error}</div>
  {/if}
</div>

<style lang="postcss">
  .field-group-stacked {
    @apply flex-col;
  }
  .dot {
    height: 10px;
    width: 10px;
    background-color: #e25c6c;
    border-radius: 50%;
    display: inline-block;
    cursor: pointer;
  }
</style>
